import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import classNames from 'classnames';
import IconButton from '@helios/IconButton';
import ButtonLink from '@helios/ButtonLink';
import ButtonGroup from '@helios/ButtonGroup';
import Card from '@helios/Card';
import Container from '@helios/Container';
import ContentBlock from '@helios/ContentBlock';
import Grid from '@helios/Grid';
import PageHero from '@helios/PageHero';
import Section from '@helios/Section';
import Layout from '@/components/Layout.js';
import StructuredData from '@/components/StructuredData.js';
import CTA from '@/components/CTA.js';
import ApplyNow from '@/components/ApplyNow.js';
import LightCopyIcon from '@/components/icons/light/copy';
import RegularPhoneIcon from '@/components/icons/regular/phone';
import { allSites, hasHeaderTicker } from '@/utils/domains.js';
import { COST } from '@/utils/programCosts.js';
import { getTuitionPageSchema } from '@/utils/seo/siteSchemas.js';
import LeadFormLink from '@/components/LeadFormLink';
import { SiteFAQSection } from '@/components/SiteFAQAccordion.js';
import { TUITION_FAQS } from '@/utils/siteFaqs.js';
import { useSiteHref } from '@/utils/siteHref';
import heroContentStyles from '@/styles/PageHeroContent.module.scss';
import styles from '@/styles/TuitionPage.module.scss';
import RaulHero from '@/public/images/admissions-landing.jpg';
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}
function Tuition(t0) {
  const $ = _c(42);
  const {
    site
  } = t0;
  const sacramentoPrograms = useSiteHref("sacramento", "/programs");
  const isSacramento = site === "sacramento";
  let t1;
  if ($[0] !== site) {
    t1 = hasHeaderTicker(site);
    $[0] = site;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !t1;
  let t3;
  if ($[2] !== t2) {
    t3 = classNames(styles.page, {
      [styles["page--flush"]]: t2
    });
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Head><title>{"Tuition Fees & Financial Aid | Campus"}</title><link rel="canonical" href="https://campus.edu/tuition" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Campus offers affordable tuition fees & financial aid options. Learn more about FAFSA, Pell Grant requirements, and how you can afford to attend college." /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isSacramento) {
    t5 = !isSacramento && <StructuredData id="tuition-page-schema" data={getTuitionPageSchema()} />;
    $[5] = isSacramento;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isSacramento) {
    t6 = classNames(styles["hero-band"], {
      [styles["hero-band--flush"]]: isSacramento
    });
    $[7] = isSacramento;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <PageHero.Content placement="split"><h1 className={classNames(heroContentStyles.title, heroContentStyles["title--display"])}>You shouldn't have to go into debt to get a great education.</h1><LeadFormLink arrow={true}>Talk to Financial Aid</LeadFormLink></PageHero.Content>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PageHero.Image src={RaulHero} alt="Raul N., Campus graduate" frame="square" priority={true} sizes="(min-width: 992px) 50vw, 100vw" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PageHero.Split mediaFirstOnMobile={false} contained={true}>{t7}<div className={styles["hero-media"]}><PageHero.Media placement="split">{t8}<PageHero.Caption><strong>Raul N.</strong><small>Campus Graduate</small></PageHero.Caption></PageHero.Media></div></PageHero.Split>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t6) {
    t10 = <PageHero><PageHero.Container><div className={t6}>{t9}</div></PageHero.Container></PageHero>;
    $[12] = t6;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      base: 1,
      xl: 2
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className={styles.eyebrow}>Associate Degree programs</p>;
    t13 = <h2 className={styles["card-title"]}>Online degrees</h2>;
    $[15] = t12;
    $[16] = t13;
  } else {
    t12 = $[15];
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Card surface="subtle" fullHeight={true} padded="lg" mobilePadded="lg" className={styles["price-card"]}><div>{t12}{t13}<p className={styles.price}><strong>{COST.quarterlyFlatRate} per quarter</strong></p><p className={styles["price-copy"]}>Students enrolled in 7–12 credits per quarter are charged a flat tuition rate of {COST.quarterlyFlatRate}. Students taking 6 credits or fewer are charged {COST.perCreditRate}{" "}per credit. Students enrolling in more than 12 credits are charged the flat rate of {COST.quarterlyFlatRate}, plus{" "}{COST.perCreditRate} per credit for each additional credit above 12.</p></div><ButtonLink href="/programs" size="sm" arrow={true}>View Programs</ButtonLink></Card>;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className={styles.eyebrow}>Certificate programs</p>;
    t16 = <h2 className={styles["card-title"]}>Certificates</h2>;
    $[18] = t15;
    $[19] = t16;
  } else {
    t15 = $[18];
    t16 = $[19];
  }
  let t17;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div>{t15}{t16}<p className={styles.price}><strong>Starting at $14,000</strong></p><p className={styles["price-copy"]}>We know that online and in-person learning offer different advantages. That's why both of our programs are engineered from the ground up to deliver focused, job-centric training and education.</p></div>;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== sacramentoPrograms) {
    t18 = <Section><Container><Grid columns={t11} gap="lg">{t14}<Card surface="subtle" fullHeight={true} padded="lg" mobilePadded="lg" className={styles["price-card"]}>{t17}<ButtonLink href={sacramentoPrograms} clientNavigation={false} size="sm" arrow={true}>View programs</ButtonLink></Card></Grid></Container></Section>;
    $[21] = sacramentoPrograms;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <h3 className={styles["callout-title"]}>If you are eligible for a full Pell Grant award, you can attend Campus with <span>no out-of-pocket tuition</span> cost.</h3>;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = classNames(styles.code, styles["code--accent"]);
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <code className={t20}>FAFSA Code: <strong>008680</strong></code>;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className={styles["code-row"]}>{t21}<IconButton icon={<LightCopyIcon />} label="Copy FAFSA code" tone="inverse" onClick={_temp2} /></div>;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <code className={styles.code}>Cal Grant Code: <strong>01291200</strong></code>;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  let t24;
  let t25;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className={styles.codes}>{t22}<div className={styles["code-row"]}>{t23}<IconButton icon={<LightCopyIcon />} label="Copy Cal Grant code" tone="inverse" onClick={_temp4} /></div></div>;
    t25 = <p>Our goal is to enable everyone to have access to a world-class education without having to take on any debt. We constantly look for ways to keep our tuition as low as possible.</p>;
    $[28] = t24;
    $[29] = t25;
  } else {
    t24 = $[28];
    t25 = $[29];
  }
  let t26;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Section><Container width="panel" gutters="compact"><ContentBlock padding="xl" rhythm="callout" surface="dark">{t19}{t24}{t25}<ButtonGroup><LeadFormLink iconRight={<RegularPhoneIcon />}>Talk to Financial Aid</LeadFormLink><ButtonLink href="https://studentaid.gov/h/apply-for-aid/fafsa" variant="outline" tone="inverse" target="_blank" rel="noopener noreferrer" arrow={true}>Start your FAFSA</ButtonLink></ButtonGroup></ContentBlock></Container></Section>;
    $[30] = t26;
  } else {
    t26 = $[30];
  }
  let t27;
  let t28;
  let t29;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <SiteFAQSection faqs={TUITION_FAQS}><p className={styles.disclaimer}><em>*Financial aid is available to those who qualify. Eligibility is determined through the Free Application for Federal Student Aid (FAFSA) and other applicable requirements. Awards may vary based on financial need, program, enrollment status, and availability of funds. Campus does not guarantee financial aid or student loan approval.</em></p></SiteFAQSection>;
    t28 = <CTA />;
    t29 = <ApplyNow />;
    $[31] = t27;
    $[32] = t28;
    $[33] = t29;
  } else {
    t27 = $[31];
    t28 = $[32];
    t29 = $[33];
  }
  let t30;
  if ($[34] !== t10 || $[35] !== t18 || $[36] !== t3 || $[37] !== t5) {
    t30 = <div className={t3}>{t4}{t5}{t10}{t18}{t26}{t27}{t28}{t29}</div>;
    $[34] = t10;
    $[35] = t18;
    $[36] = t3;
    $[37] = t5;
    $[38] = t30;
  } else {
    t30 = $[38];
  }
  let t31;
  if ($[39] !== site || $[40] !== t30) {
    t31 = <Layout site={site}>{t30}</Layout>;
    $[39] = site;
    $[40] = t30;
    $[41] = t31;
  } else {
    t31 = $[41];
  }
  return t31;
}
function _temp4() {
  return navigator.clipboard.writeText("01291200").catch(_temp3);
}
function _temp3() {}
function _temp2() {
  return navigator.clipboard.writeText("008680").catch(_temp);
}
function _temp() {}
export default Tuition;